<script>
  import './styles/global.css';
  import { onMount } from 'svelte';
  import LoginModal from './modals/LoginModal.svelte';
  
  import MyReviews from './pages/MyReviews.svelte';
  import Manage from './pages/Manage.svelte';
  import AccountModal from './modals/AccountModal.svelte';
  import PastShows from './pages/PastShows.svelte';
  import UpcomingShows from './pages/UpcomingShows.svelte';
  import NewsPage from './pages/NewsPage.svelte';
  import About from './pages/About.svelte';
  import CustomReviews from './pages/CustomReviews.svelte';
  import { isLoggedIn, user, initAuth, logout, apiFetch, maybeRefresh } from './lib/auth.js';
  import { customReviewsEnabled } from './lib/customReviewsCookie.js';

  // browse pages
  const BROWSE_PAGES = ['shows', 'past', 'news'];
  const BROWSE_TABS = [
    { key: 'shows',   label: 'Upcoming Shows'   },
    { key: 'past', label: 'Past Shows' },
    { key: 'news',    label: 'News'    },
  ];

  let activePage   = 'home';
  let titleVisible = false;
  let showLogin = false, showAccount = false;
  let reviewRefresh = 0;


  $: isBrowsePage = BROWSE_PAGES.includes(activePage);

  let kpis = [
    { label: 'Reviews', value: '\u2026' },
    { label: 'Active Users', value: '\u2026' },
  ];

  onMount(async () => {
    setTimeout(() => titleVisible = true, 100);
    await initAuth();
    maybeRefresh();
    try {
      const stats = await apiFetch('/statistics');
      kpis = [
        { label: 'Reviews',      value: stats.total_reviews.toLocaleString() },
        { label: 'Last 30 Days', value: stats.reviews_last_30d.toLocaleString() },
        { label: 'Active Users', value: stats.active_reviewers_last_30d.toLocaleString() },
      ];
    } catch {}
  });

  function navigate(page) {
    activePage = page;
    maybeRefresh();
  }

  function handleLogout() { logout(); }
</script>

<div class="app">
  <header class="header">
    <div class="header-bg"></div>
    <div class="header-content" class:visible={titleVisible}>
      <div class="skull-deco">
        <svg viewBox="0 0 40 40" fill="none">
          <ellipse cx="20" cy="17" rx="12" ry="12" stroke="#3a6b00" stroke-width="1.5"/>
          <rect x="14" y="26" width="12" height="8" rx="1" stroke="#3a6b00" stroke-width="1.5"/>
          <line x1="20" y1="26" x2="20" y2="34" stroke="#3a6b00" stroke-width="1"/>
          <line x1="14" y1="30" x2="26" y2="30" stroke="#3a6b00" stroke-width="1"/>
          <circle cx="15" cy="16" r="3" fill="#3a6b00" opacity="0.7"/>
          <circle cx="25" cy="16" r="3" fill="#3a6b00" opacity="0.7"/>
        </svg>
      </div>
      <div class="title-block">
        <h1 class="site-title">Death Metal Diary</h1>
        <div class="title-underline"></div>
        <p class="tagline">Death, Unicorns, Whisky and Blergh</p>
      </div>
      <div class="header-auth">
        {#if $isLoggedIn}
          <div class="user-chip">
            <button class="user-name-btn" on:click={() => showAccount = true}>
              <span class="user-name">{$user?.username || $user?.sub || 'user'}</span>
            </button>
            <button class="auth-btn logout" on:click={handleLogout}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </button>
          </div>
        {:else}
          <button class="auth-btn login" on:click={() => showLogin = true}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
              <polyline points="10 17 15 12 10 7"/>
              <line x1="15" y1="12" x2="3" y2="12"/>
            </svg>
            <span>Login</span>
          </button>
        {/if}
      </div>
    </div>
  </header>

  <main class="main">

    <!-- KPI bar — always visible on home and all browse pages -->
    {#if activePage === 'home' || isBrowsePage}
      <div class="kpi-bar">
        {#each kpis as kpi, i}
          <div class="kpi" style="animation-delay:{i*0.1}s">
            <span class="kpi-value">{kpi.value}</span>
            <span class="kpi-label">{kpi.label}</span>
          </div>
          {#if i < kpis.length - 1}<div class="kpi-div"></div>{/if}
        {/each}
      </div>
    {/if}

    <!-- Browse tab bar — shown when on any browse page -->
    {#if isBrowsePage}
      <div class="browse-tabs">
        {#each BROWSE_TABS as tab}
          <button
            class="browse-tab"
            class:active={activePage === tab.key}
            on:click={() => navigate(tab.key)}
          >{tab.label}</button>
        {/each}
      </div>
    {/if}

    {#key activePage}

      {#if activePage === 'home'}
        <div class="page">
          <div class="home-grid">
            <button class="tile" on:click={() => navigate('shows')}>
              <div class="tile-glow"></div>
              <div class="tile-corner tl"></div><div class="tile-corner br"></div>
              <svg class="tile-icon" viewBox="0 0 48 32" fill="none">
                <rect x="6" y="6" width="36" height="22" rx="2" stroke="#2a5200" stroke-width="1.5"/>
                <line x1="6" y1="13" x2="42" y2="13" stroke="#2a5200" stroke-width="1.5"/>
                <line x1="16" y1="4" x2="16" y2="9" stroke="#2a5200" stroke-width="1.5" stroke-linecap="round"/>
                <line x1="32" y1="4" x2="32" y2="9" stroke="#2a5200" stroke-width="1.5" stroke-linecap="round"/>
                <rect x="13" y="17" width="6" height="5" rx="1" fill="#1e3d00"/>
                <rect x="21" y="17" width="6" height="5" rx="1" fill="#1e3d00" opacity="0.6"/>
              </svg>
              <span class="tile-label">Upcoming Shows</span>
            </button>

            <button class="tile" on:click={() => navigate('past')}>
              <div class="tile-glow"></div>
              <div class="tile-corner tl"></div><div class="tile-corner br"></div>
              <svg class="tile-icon" viewBox="0 0 48 32" fill="none">
                <circle cx="16" cy="13" r="6" stroke="#2a5200" stroke-width="1.5"/>
                <path d="M4 28c0-6.627 5.373-10 12-10s12 3.373 12 10" stroke="#2a5200" stroke-width="1.5"/>
                <circle cx="34" cy="13" r="5" stroke="#1e3d00" stroke-width="1.5"/>
                <path d="M30 28c0-5 3-8 8-8" stroke="#1e3d00" stroke-width="1.5"/>
              </svg>
              <span class="tile-label">Past Shows</span>
            </button>

            <button class="tile" on:click={() => navigate('news')}>
              <div class="tile-glow"></div>
              <div class="tile-corner tl"></div><div class="tile-corner br"></div>
              <svg class="tile-icon" viewBox="0 0 48 32" fill="none">
                <rect x="6" y="4" width="36" height="26" rx="2" stroke="#2a5200" stroke-width="1.5"/>
                <line x1="12" y1="12" x2="36" y2="12" stroke="#2a5200" stroke-width="1.5"/>
                <line x1="12" y1="17" x2="36" y2="17" stroke="#1e3d00" stroke-width="1.5"/>
                <line x1="12" y1="22" x2="24" y2="22" stroke="#1e3d00" stroke-width="1"/>
                <circle cx="38" cy="8" r="3" fill="#1a3300" stroke="#3a6b00" stroke-width="1"/>
                <line x1="36.8" y1="8" x2="39.2" y2="8" stroke="#6db800" stroke-width="1"/>
                <line x1="38" y1="6.8" x2="38" y2="9.2" stroke="#6db800" stroke-width="1"/>
              </svg>
              <span class="tile-label">News</span>
            </button>

            <button class="tile" on:click={() => navigate('info')}>
              <div class="tile-glow"></div>
              <div class="tile-corner tl"></div><div class="tile-corner br"></div>
              <svg class="tile-icon" viewBox="0 0 48 32" fill="none">
                <circle cx="24" cy="14" r="10" stroke="#2a5200" stroke-width="1.5"/>
                <line x1="24" y1="13" x2="24" y2="21" stroke="#2a5200" stroke-width="1.5" stroke-linecap="round"/>
                <circle cx="24" cy="9" r="1.5" fill="#2a5200"/>
              </svg>
              <span class="tile-label">About &amp; Privacy</span>
            </button>

            <!-- Custom Reviews tile — only visible when feature cookie is set -->
            {#if $customReviewsEnabled && $isLoggedIn}
              <button class="tile tile-custom" on:click={() => navigate('custom-reviews')}>
                <div class="tile-glow"></div>
                <div class="tile-corner tl"></div><div class="tile-corner br"></div>
                <svg class="tile-icon" viewBox="0 0 48 32" fill="none">
                  <!-- cigar shape -->
                  <rect x="6" y="13" width="30" height="6" rx="3" stroke="#2a5200" stroke-width="1.5"/>
                  <!-- ash tip -->
                  <rect x="34" y="14.5" width="6" height="3" rx="1" fill="#1e3d00" stroke="#3a6b00" stroke-width="1"/>
                  <!-- smoke wisps -->
                  <path d="M37 14 Q39 11 37 8" stroke="#2a5200" stroke-width="1" stroke-linecap="round" fill="none" opacity="0.7"/>
                  <path d="M40 14 Q42 10 40 7" stroke="#1e3d00" stroke-width="1" stroke-linecap="round" fill="none" opacity="0.5"/>
                  <!-- ring band -->
                  <line x1="18" y1="13" x2="18" y2="19" stroke="#3a6b00" stroke-width="1"/>
                </svg>
                <span class="tile-label">My Collection</span>
              </button>
            {/if}
          </div>
        </div>

      {:else if activePage === 'reviews'}
        <div class="page">
          <MyReviews
            refreshTrigger={reviewRefresh}
            loggedIn={$isLoggedIn}
            on:login={() => showLogin = true}
          />
        </div>

      {:else if activePage === 'past'}
        <div class="page">
          <PastShows loggedIn={$isLoggedIn} />
        </div>

      {:else if activePage === 'shows'}
        <div class="page">
          <UpcomingShows loggedIn={$isLoggedIn} />
        </div>

      {:else if activePage === 'news'}
        <div class="page">
          <NewsPage />
        </div>

      {:else if activePage === 'manage'}
        <div class="page">
          <Manage />
        </div>

      {:else if activePage === 'info'}
        <About />

      {:else if activePage === 'custom-reviews'}
        <div class="page">
          <CustomReviews />
        </div>
      {/if}

    {/key}
  </main>

  <nav class="navbar">
    <button class="nav-item" class:active={activePage === 'home'} on:click={() => navigate('home')}>
      <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
        <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
        <polyline points="9 22 9 12 15 12 15 22"/>
      </svg>
      <span class="nav-label">Home</span>
    </button>
    <button class="nav-item" class:active={activePage === 'reviews'} on:click={() => navigate('reviews')}>
      <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
        <path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
      </svg>
      <span class="nav-label">Reviews</span>
    </button>
    <button class="nav-item" class:active={activePage === 'manage'} on:click={() => navigate('manage')}>
      <svg class="nav-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
      <span class="nav-label">Manage</span>
    </button>
  </nav>
</div>

<LoginModal open={showLogin} on:close={() => showLogin = false} />
<AccountModal  open={showAccount}  on:close={() => showAccount  = false} />

<style>
  @import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;600;700&display=swap');
  :global(*, *::before, *::after) { box-sizing: border-box; margin: 0; padding: 0; }
  :global(body) {
    background: #0a0a0a; color: #c8c8c8; font-family: 'Rajdhani', sans-serif;
    min-height: 100vh; overflow-x: hidden; overflow-y: auto;
  }
  .app {
    display: flex; flex-direction: column; min-height: 100vh;
    background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(60,100,0,0.015) 2px, rgba(60,100,0,0.015) 4px), #0a0a0a;
  }
  .header {
    position: relative; flex-shrink: 0; padding: 1.25rem 1rem 1rem;
    overflow: hidden; border-bottom: 2px solid #3a6b00; box-shadow: 0 4px 30px rgba(74,124,0,0.4);
  }
  .header-bg {
    position: absolute; inset: 0;
    background: radial-gradient(ellipse at 50% -20%, rgba(74,124,0,0.35) 0%, transparent 70%), linear-gradient(180deg, #0d1a00 0%, #0a0a0a 100%);
    z-index: 0;
  }
  .header::after {
    content: ''; position: absolute; bottom: -6px; left: 0; right: 0; height: 6px;
    background: repeating-linear-gradient(90deg, #3a6b00 0px, #3a6b00 8px, transparent 8px, transparent 14px);
    clip-path: polygon(0% 0%,3% 100%,6% 0%,9% 100%,12% 0%,15% 100%,18% 0%,21% 100%,24% 0%,27% 100%,30% 0%,33% 100%,36% 0%,39% 100%,42% 0%,45% 100%,48% 0%,51% 100%,54% 0%,57% 100%,60% 0%,63% 100%,66% 0%,69% 100%,72% 0%,75% 100%,78% 0%,81% 100%,84% 0%,87% 100%,90% 0%,93% 100%,96% 0%,100% 100%,100% 0%);
  }
  .header-content {
    position: relative; z-index: 1; display: flex; align-items: center; justify-content: space-between;
    opacity: 0; transform: translateY(-10px); transition: opacity 0.8s ease, transform 0.8s ease;
  }
  .header-content.visible { opacity: 1; transform: translateY(0); }
  .skull-deco {
    width: 40px; height: 40px; flex-shrink: 0; opacity: 0.7;
    filter: drop-shadow(0 0 8px rgba(74,124,0,0.8)); animation: skull-pulse 3s ease-in-out infinite;
  }
  @keyframes skull-pulse {
    0%,100% { opacity:0.7; filter: drop-shadow(0 0 8px rgba(74,124,0,0.8)); }
    50%      { opacity:1;   filter: drop-shadow(0 0 16px rgba(100,180,0,1)); }
  }
  .title-block { display: flex; flex-direction: column; align-items: center; gap: 0.25rem; flex: 1; }
  .site-title {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: clamp(1.6rem, 5vw, 3rem); color: #fff;
    text-shadow: 0 0 10px rgba(74,124,0,0.9), 0 0 30px rgba(74,124,0,0.5), 2px 2px 0 #1a2e00;
    letter-spacing: 0.1em; line-height: 1; text-transform: uppercase;
  }
  .title-underline {
    width: 80%; height: 2px;
    background: linear-gradient(90deg, transparent, #3a6b00, #6db800, #3a6b00, transparent);
    box-shadow: 0 0 8px rgba(74,124,0,0.8);
  }
  .tagline {
    font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 0.6rem;
    letter-spacing: 0.3em; text-transform: uppercase; color: #4a7c00; text-shadow: 0 0 6px rgba(74,124,0,0.6);
  }
  .header-auth { flex-shrink: 0; display: flex; align-items: center; }
  .auth-btn {
    display: flex; align-items: center; gap: 0.35rem; background: none; border: 1px solid #1e3d00;
    color: #3a6b00; cursor: pointer; font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 0.55rem; letter-spacing: 0.2em; text-transform: uppercase;
    padding: 0.45rem 0.7rem; border-radius: 2px; transition: color 0.2s, border-color 0.2s, background 0.2s;
  }
  .auth-btn svg { width: 16px; height: 16px; flex-shrink: 0; }
  .auth-btn.login:hover  { color: #6db800; border-color: #3a6b00; background: rgba(74,124,0,0.08); }
  .auth-btn.logout:hover { color: #cc4400; border-color: #5a2000; background: rgba(204,68,0,0.08); }
  .auth-btn.logout { padding: 0.4rem; }
  .user-chip { display: flex; align-items: center; gap: 0.4rem; }
  .user-name-btn { background: none; border: none; cursor: pointer; padding: 0; transition: opacity 0.2s; }
  .user-name-btn:hover { opacity: 0.75; }
  .user-name {
    font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 0.6rem;
    letter-spacing: 0.15em; text-transform: uppercase; color: #6db800;
    max-width: 80px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; display: block;
  }
  .main { flex: 1; }
  .page { padding: 1.25rem 1rem 6rem; }
  .page-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 1rem; }
  .page-title {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.7rem; color: #6db800;
    letter-spacing: 0.25em; text-transform: uppercase; border-left: 3px solid #3a6b00; padding-left: 0.75rem;
  }
  .add-btn {
    display: flex; align-items: center; gap: 0.3rem; background: #1a3300; border: 1px solid #3a6b00;
    color: #6db800; font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.6rem;
    letter-spacing: 0.2em; text-transform: uppercase; padding: 0.45rem 0.8rem; border-radius: 2px;
    cursor: pointer; transition: background 0.2s, box-shadow 0.2s;
  }
  .add-btn:hover { background: #223d00; box-shadow: 0 0 8px rgba(109,184,0,0.3); }
  .add-btn svg { width: 14px; height: 14px; }
  .kpi-bar {
    display: flex; align-items: center; justify-content: space-between; background: #0f0f0f;
    border-bottom: 1px solid #1e3d00; padding: 0.75rem 1rem;
    box-shadow: inset 0 0 20px rgba(74,124,0,0.05);
  }
  .kpi { display: flex; flex-direction: column; align-items: center; gap: 0.1rem; animation: fadeDown 0.5s ease forwards; }
  @keyframes fadeDown { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
  .kpi-value {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 1.6rem; line-height: 1;
    color: #6db800; text-shadow: 0 0 10px rgba(109,184,0,0.6); letter-spacing: 0.05em;
  }
  .kpi-label { font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 0.55rem; letter-spacing: 0.2em; text-transform: uppercase; color: #3a6b00; }
  .kpi-div { width: 1px; height: 2rem; background: linear-gradient(180deg, transparent, #3a6b00, transparent); }
  .home-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.75rem; }
  .tile {
    position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 0.4rem; padding: 1.5rem 1rem; background: #0f0f0f; border: 1px solid #1e3d00; border-radius: 3px;
    cursor: pointer; overflow: hidden; transition: border-color 0.2s, transform 0.15s; animation: fadeUp 0.4s ease forwards;
  }
  .tile:nth-child(1) { animation-delay: 0s; }
  .tile:nth-child(2) { animation-delay: 0.07s; }
  .tile:nth-child(3) { animation-delay: 0.14s; }
  .tile:nth-child(4) { animation-delay: 0.21s; }
  .tile:nth-child(5) { animation-delay: 0.28s; }
  @keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
  .tile:last-child:nth-child(odd) { grid-column: span 2; }
  .tile:hover { border-color: #3a6b00; transform: translateY(-2px); }
  .tile:hover .tile-glow { opacity: 1; }
  .tile:active { transform: translateY(0); }
  .tile-corner { position: absolute; width: 10px; height: 10px; border-color: #2a5200; border-style: solid; transition: border-color 0.2s; }
  .tile:hover .tile-corner { border-color: #6db800; }
  .tile-corner.tl { top: 5px; left: 5px; border-width: 1px 0 0 1px; }
  .tile-corner.br { bottom: 5px; right: 5px; border-width: 0 1px 1px 0; }
  .tile-glow {
    position: absolute; inset: 0;
    background: radial-gradient(ellipse at 50% 100%, rgba(74,124,0,0.12) 0%, transparent 70%);
    opacity: 0; transition: opacity 0.3s; pointer-events: none;
  }
  .tile-icon { width: 48px; height: 32px; transition: filter 0.2s; }
  .tile:hover .tile-icon { filter: drop-shadow(0 0 6px rgba(109,184,0,0.5)); }
  .tile-label {
    font-family: 'Rajdhani', sans-serif; font-weight: 700; font-size: 0.75rem;
    letter-spacing: 0.15em; text-transform: uppercase; color: #c8c8c8; text-align: center; line-height: 1.2;
  }
  /* Custom reviews tile gets a subtle amber tint to differentiate it */
  .tile-custom { border-color: #2a1f00; }
  .tile-custom:hover { border-color: #6b5200; }
  .tile-custom .tile-corner { border-color: #3d2e00; }
  .tile-custom:hover .tile-corner { border-color: #c8900a; }
  .tile-custom .tile-glow {
    background: radial-gradient(ellipse at 50% 100%, rgba(124,90,0,0.12) 0%, transparent 70%);
  }
  .tile-custom .tile-label { color: #b8a060; }
  .tile-custom:hover .tile-label { color: #d4b870; }

  /* ── Browse tab bar ── */
  .browse-tabs {
    display: flex; border-bottom: 1px solid #1e3d00;
    background: #0a0a0a; position: sticky; top: 0; z-index: 50;
    box-shadow: 0 2px 12px rgba(0,0,0,0.5);
  }
  .browse-tab {
    flex: 1; background: none; border: none; border-bottom: 2px solid transparent;
    margin-bottom: -1px; color: #2a5200; cursor: pointer;
    font-family: 'Rajdhani', sans-serif; font-weight: 700;
    font-size: 0.6rem; letter-spacing: 0.2em; text-transform: uppercase;
    padding: 0.75rem 0.5rem; transition: color 0.15s, border-color 0.15s;
  }
  .browse-tab:hover { color: #4a7c00; }
  .browse-tab.active { color: #6db800; border-bottom-color: #6db800; }

  .navbar {
    position: fixed; bottom: 0; left: 0; right: 0; display: flex; background: #0f0f0f;
    border-top: 1px solid #1e3d00; box-shadow: 0 -4px 20px rgba(74,124,0,0.2);
    z-index: 100; padding-bottom: env(safe-area-inset-bottom);
  }
  .nav-item {
    flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center;
    gap: 0.25rem; padding: 0.75rem 0.5rem; background: none; border: none; cursor: pointer;
    color: #333; transition: color 0.2s ease; position: relative;
  }
  .nav-item::before {
    content: ''; position: absolute; top: 0; left: 50%; transform: translateX(-50%) scaleX(0);
    width: 60%; height: 2px; background: #6db800; box-shadow: 0 0 8px rgba(109,184,0,0.8); transition: transform 0.25s ease;
  }
  .nav-item.active::before { transform: translateX(-50%) scaleX(1); }
  .nav-item.active { color: #6db800; }
  .nav-item:hover:not(.active) { color: #4a7c00; }
  .nav-icon { width: 20px; height: 20px; stroke: currentColor; transition: filter 0.2s; }
  .nav-item.active .nav-icon { filter: drop-shadow(0 0 4px rgba(109,184,0,0.6)); }
  .nav-label { font-family: 'Rajdhani', sans-serif; font-weight: 600; font-size: 0.5rem; letter-spacing: 0.12em; text-transform: uppercase; line-height: 1; }
</style>
